import { defaultAxios } from '@/services/http';
import router from '@/router';
import Vue from 'vue';
import store from '../store/store.js';
import { setSentryUser, clearSentryUser } from '@/services/sentry';
import * as Sentry from '@sentry/vue';

let isAlreadyFetchingAccessToken = false;
let subscribers = [];

function addSubscriber(subscriber) {
  subscribers.push(subscriber);
}

function onSessionReprotected(data) {
  // Release the refresh lock on success. Without this, the flag stays `true`
  // for the rest of the page's lifetime and every subsequent SESSION_EXPIRED
  // deadlocks (request queued to `subscribers`, no refresh ever dispatched).
  isAlreadyFetchingAccessToken = false;
  localStorage.setItem('sessionToken', data.sessionToken);
  localStorage.setItem('protectionKey', data.protectionKey);
  Vue.prototype.$http.defaults.headers.common['Authorization'] = `Bearer ${data.sessionToken}`;
  defaultAxios.defaults.headers.common['Authorization'] = `Bearer ${data.sessionToken}`;
  const queued = subscribers;
  subscribers = [];
  queued.forEach(({ resolve, originalRequest }) => {
    originalRequest.headers.Authorization = `Bearer ${data.sessionToken}`;
    resolve(defaultAxios(originalRequest));
  });
}

// Transient refresh failure (network/timeout/5xx) whose retries were exhausted.
// Do NOT log out — the current token is still valid (server refresh is in-place),
// so a momentary blip shouldn't kick the user. Release the lock and reject the
// queued requests so they don't hang; the next request triggers a fresh attempt.
function onSessionRefreshFailed(error) {
  isAlreadyFetchingAccessToken = false;
  const queued = subscribers;
  subscribers = [];
  queued.forEach(({ reject }) => reject(error));
  Sentry.captureMessage('session_refresh_failed', { level: 'error', extra: { message: error && error.message } });
}

export default {
  async init() {
    if (localStorage['sessionToken'] !== undefined) {
      defaultAxios.defaults.headers.common['Authorization'] = `Bearer ${localStorage['sessionToken']}`;
    }

    if (localStorage.getItem('userInfo')) {
      setSentryUser(JSON.parse(localStorage.getItem('userInfo')));
    }

    defaultAxios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const { config, response } = error;
        const originalRequest = config;

        if (response && response.status === 401) {
          if (response.data.type === 'UNAUTHORIZED_ACCESS') {
            Sentry.captureMessage('forced_logout', 'warning');
            return this.logout();
          }
          if (response.data.type === 'SESSION_EXPIRED') {
            if (isAlreadyFetchingAccessToken === false) {
              isAlreadyFetchingAccessToken = true;
              store
                .dispatch('auth/fetchAccessToken')
                .then((response) => {
                  onSessionReprotected(response.data);
                })
                .catch((refreshError) => {
                  // A definitive auth failure already logged out inside
                  // reprotectSession. Anything reaching here is a transient
                  // failure whose retries were exhausted — release the lock and
                  // reject the queued requests WITHOUT logging out, so users
                  // aren't kicked by a momentary network blip mid-session.
                  onSessionRefreshFailed(refreshError);
                });
            }

            return new Promise((resolve, reject) => {
              addSubscriber({ resolve, reject, originalRequest });
            });
          }
        }
        return Promise.reject(error);
      },
    );
  },

  createLoginRequest(email, method, turnstileToken) {
    return defaultAxios.post('auth/login/createLoginRequest', { email, method, turnstileToken });
  },
  refreshUserObject() {
    return defaultAxios.get('user/@me');
  },
  refreshCourseObject() {
    return defaultAxios.get('user/@me/activeCourse');
  },
  getLoginMethods(email, turnstileToken) {
    return defaultAxios.post('auth/login/getLoginMethods', { email, turnstileToken });
  },
  loginStandard(loginToken, challengeCode, password, turnstileToken) {
    return defaultAxios.post('auth/login/submitLoginRequest', { loginToken, challengeCode, password, turnstileToken });
  },
  loginMagicLink(magicToken, turnstileToken) {
    return defaultAxios.post('auth/magiclink/useMagicLink', { magicToken, turnstileToken });
  },
  logout(api = false) {
    console.log('Logging Out');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('courseInfo');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('protectionKey');
    delete defaultAxios.defaults.headers.common['Authorization'];
    clearSentryUser();
    router.push('/login');
  },
  registerUser(first_name, last_name, email, password, recaptcha) {
    return defaultAxios.post('user/register', {
      first_name,
      last_name,
      email,
      password,
      recaptcha,
    });
  },
  async reprotectSession(retries = 3) {
    for (let attempt = 0; ; attempt += 1) {
      let response;
      try {
        response = await defaultAxios.post('auth/session/refreshSession', {
          protectionKey: localStorage.getItem('protectionKey'),
        });
      } catch (error) {
        // Only TRANSIENT failures (network/timeout/5xx) reject here — the
        // interceptor turns a definitive 401 UNAUTHORIZED_ACCESS into a logout()
        // before this runs. Retry with backoff; on exhaustion, give up WITHOUT
        // logging out (the current token is still valid).
        if (attempt >= retries) throw error;
        await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt));
        continue;
      }
      // Definitive auth failure: the interceptor already logged out and resolved
      // with no payload. Stop — don't retry, don't log out again.
      if (!response || !response.data || !response.data.sessionToken) {
        throw new Error('reprotectSession: session invalidated');
      }
      return response;
    }
  },
};
